import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaInfoCircle, FaShareAlt, FaTrashAlt } from 'react-icons/fa';
import { MdSecurity } from 'react-icons/md';
import { createApiClient } from '../../api/client';
import '../mobile.css';

const CACHE_KEY = 'mycases';
const SELECTED_KEY = 'mycases_selected';

const formatDateTime = (dateTime) => {
  if (!dateTime) return 'N/A';
  return `${dateTime.substring(0, 2)}-${dateTime.substring(2, 4)}-${dateTime.substring(4, 8)}`;
};

const parseDateTime = (dateTime) => {
  if (!dateTime) return 0;
  const day = parseInt(dateTime.substring(0, 2), 10);
  const month = parseInt(dateTime.substring(2, 4), 10) - 1;
  const year = parseInt(dateTime.substring(4, 8), 10);
  const hours = parseInt(dateTime.substring(9, 11), 10) || 0;
  const minutes = parseInt(dateTime.substring(12, 14), 10) || 0;
  return new Date(year, month, day, hours, minutes).getTime();
};

const sortFirList = (list) => [...list].sort((a, b) => parseDateTime(b.uploadedOn) - parseDateTime(a.uploadedOn));

const MobileMyCases = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cases, setCases] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firToDelete, setFirToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const email = localStorage.getItem('email');

  const fetchCases = async (force) => {
    if (!force) {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          setCases(JSON.parse(cached));
          setLoading(false);
          return;
        } catch {
          // fall through to a fresh fetch if the cached value is corrupt
        }
      }
    }

    setLoading(true);
    setError(null);

    try {
      const api = createApiClient(API_URL);
      const response = await api.get('/fir/list');
      const formatted = (response.data || []).map((c) => ({
        ...c,
        sections: c.firSections ? `${c.firSections[0].number} ${c.firSections[0].act}` : '-',
      }));
      const sorted = sortFirList(formatted);
      setCases(sorted);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(sorted));
      } catch {
        // Storage quota exceeded (common on iOS Safari/WebKit) - the fetch
        // itself succeeded, so just skip caching rather than fail the load.
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/m/login');
        return;
      }
      console.error('fir/list failed:', err);
      const detail = err?.message || 'Unknown error';
      setError(`Could not load your cases. (${detail})`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // A fresh case just created/uploaded elsewhere means the cache is stale.
    fetchCases(Boolean(location.state?.reloadCases));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the last-selected card's on-screen position and highlight once the
  // list has rendered, so returning from a case screen feels like you never left.
  useEffect(() => {
    if (!cases || cases.length === 0) return;

    const stored = localStorage.getItem(SELECTED_KEY);
    if (!stored) return;

    let selected;
    try {
      selected = JSON.parse(stored);
    } catch {
      return;
    }

    const container = document.getElementById('m-scroll-content');
    const cardEl = container?.querySelector(`[data-case-id="${selected.id}"]`);
    if (!container || !cardEl) return;

    const cardTopWithinContainer = cardEl.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTop = cardTopWithinContainer - (selected.offset || 0);
  }, [cases]);

  const handleRefresh = () => fetchCases(true);

  const selectedId = (() => {
    try {
      return JSON.parse(localStorage.getItem(SELECTED_KEY) || 'null')?.id;
    } catch {
      return null;
    }
  })();

  // Remembers which card the user acted on and its on-screen position, so we
  // can scroll back to the same spot and highlight it when they return here.
  const selectCase = (fir) => {
    const container = document.getElementById('m-scroll-content');
    const cardEl = container?.querySelector(`[data-case-id="${fir.id}"]`);
    const offset = container && cardEl
      ? cardEl.getBoundingClientRect().top - container.getBoundingClientRect().top
      : 0;
    localStorage.setItem(SELECTED_KEY, JSON.stringify({ id: fir.id, offset }));
  };

  const closeDeletePopup = () => {
    setFirToDelete(null);
    setIsDeleting(false);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!firToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const api = createApiClient(API_URL);
      await api.delete(`/fir/${firToDelete.id}`);

      const updated = (cases || []).filter((fir) => fir.id !== firToDelete.id);
      setCases(updated);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
      } catch {
        localStorage.removeItem(CACHE_KEY);
      }
      closeDeletePopup();
    } catch (err) {
      setIsDeleting(false);
      if (err?.response?.status === 401) {
        setDeleteError('You are not authorised to delete the FIR.');
      } else {
        setDeleteError('An error occurred while deleting the FIR. Please try again later.');
      }
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
        <div className="m-spinner" style={{ width: '32px', height: '32px', borderColor: '#eee', borderTopColor: '#094268' }} />
        <p>Loading your cases...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: 'red' }}>{error}</p>
        <button className="m-button-primary" style={{ width: 'auto', padding: '10px 20px' }} onClick={handleRefresh}>Retry</button>
      </div>
    );
  }

  if (!cases || cases.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ fontWeight: 700 }}>You have no cases uploaded yet.</p>
        <button className="m-button-primary" style={{ width: '90%', marginTop: '10px' }} onClick={() => navigate('/m/cases/new')}>Upload Case</button>
        <p style={{ marginTop: '20px' }}>
          <button onClick={handleRefresh} className="m-pill-btn" style={{ color: '#007AFF', fontWeight: 600 }}>Refresh</button>
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '60vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#094268' }}>Case List</span>
        <button onClick={handleRefresh} className="m-pill-btn" style={{ color: '#007AFF', fontWeight: 600 }}>Refresh</button>
      </div>

      {cases.map((fir) => (
        <div
          key={fir.id}
          data-case-id={fir.id}
          className="m-card"
          onClick={() => { selectCase(fir); navigate('/m/fir/dashboard', { state: { fir } }); }}
          style={{ backgroundColor: fir.id === selectedId ? '#D6EAF8' : undefined, cursor: 'pointer' }}
        >
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
            {fir.complainant?.name || '-'}
          </div>
          <div style={{ fontWeight: 'bold', color: '#8C1007', marginBottom: '3px' }}>
            {fir.caseType || 'Matrimonial'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <span style={{ color: '#555', fontSize: '15px' }}>{formatDateTime(fir.uploadedOn)}</span>
            {/* <span style={{ color: '#427c61', fontSize: '15px' }}>
              {fir.user === email && fir.voucherCode ? fir.voucherCode : 'Sample'}
            </span> */}
          </div>

          <div style={{ height: '1px', background: '#e0e0e0', margin: '10px 0 5px' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 4px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); selectCase(fir); navigate('/m/cases/details', { state: { fir } }); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              title="Case Details"
            >
              <FaInfoCircle size={20} color="#387095" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); selectCase(fir); navigate('/m/fir/dashboard', { state: { fir } }); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              title="Case Dashboard"
            >
              <MdSecurity size={24} color="#094268" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); selectCase(fir); navigate('/m/cases/share', { state: { fir } }); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              title="Share Case"
            >
              <FaShareAlt size={18} color="#2a8088" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setFirToDelete(fir); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              title="Delete Case"
            >
              <FaTrashAlt size={18} color="#c9302c" />
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={() => navigate('/m/cases/new')}
        className="m-chat-fab"
        style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
        aria-label="New case"
      >
        <FaPlus size={22} color="#fff" />
      </button>

      {firToDelete && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>{deleteError ? 'Delete Failed' : 'Confirm Delete'}</h3>
            {deleteError ? (
              <>
                <p style={{ color: '#c9302c' }}>{deleteError}</p>
                <button className="m-button-primary" onClick={closeDeletePopup}>OK</button>
              </>
            ) : (
              <>
                <p style={{ textAlign: 'justify' }}>
                  Are you sure you want to delete the {firToDelete.caseType || 'selected'} FIR lodged by {firToDelete.complainant?.name || '-'}?
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button onClick={closeDeletePopup} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }} disabled={isDeleting}>Cancel</button>
                  <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={confirmDelete} disabled={isDeleting}>
                    {isDeleting ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileMyCases;
